import { useState, useEffect, useRef, useCallback } from "react";
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from "date-fns";
import { ChevronLeft, ChevronRight, Plus, XCircle, ClipboardList, StickyNote, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const ROW_HEIGHT = 24;

const timeToMinutes = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const minutesToTime = (mins) => {
  const clamped = Math.max(0, Math.min(mins, 1410));
  const h = Math.floor(clamped / 60);
  let m = Math.round(clamped % 60 / 30) * 30;
  if (m === 60) m = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const SHIFT_COLOR_MAP = {
  orange: 'bg-orange-200 border-orange-400 text-orange-900',
  blue: 'bg-blue-200 border-blue-400 text-blue-900',
  indigo: 'bg-indigo-200 border-indigo-400 text-indigo-900',
  green: 'bg-green-200 border-green-400 text-green-900',
  purple: 'bg-purple-200 border-purple-400 text-purple-900',
  red: 'bg-red-200 border-red-400 text-red-900',
  yellow: 'bg-yellow-200 border-yellow-400 text-yellow-900',
  pink: 'bg-pink-200 border-pink-400 text-pink-900',
  teal: 'bg-teal-200 border-teal-400 text-teal-900',
  gray: 'bg-gray-200 border-gray-400 text-gray-900',
};

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 0; h <= 23; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  return slots;
})();

export default function WeekTimelineView({
  currentDate, shifts, absences, workers, shiftTypes, tasks, reminders,
  dateLocale, t,
  onPrevWeek, onNextWeek,
  onShiftClick, onAddShift, onAddAbsence, onAddTask, onAddNote, onAddReminder,
  onEditTask, onEditReminder,
  onUpdateItemTime,
}) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getShiftTime = (shift) => {
    if (shift.custom_start_time && shift.custom_end_time) return { start: shift.custom_start_time, end: shift.custom_end_time };
    const st = shiftTypes.find(s => s.id === shift.shift_type_id);
    if (!st || st.start_time === '--:--') return null;
    return { start: st.start_time, end: st.end_time };
  };

  const getShiftColor = (shift) => {
    const st = shiftTypes.find(s => s.id === shift.shift_type_id);
    return SHIFT_COLOR_MAP[st?.color] || SHIFT_COLOR_MAP.blue;
  };

  const getWorkerName = (wid) => workers.find(w => w.id === wid)?.name || '';
  const isToday = (day) => format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowTop = (nowMinutes / 30) * ROW_HEIGHT;

  // ========== DRAG & RESIZE SYSTEM ==========
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);
  const scrollRef = useRef(null);
  const didDragRef = useRef(false);

  const startDrag = useCallback((e, item, itemType, action, originalDate) => {
    e.stopPropagation();
    e.preventDefault();
    didDragRef.current = false;

    let startMins, endMins;
    if (itemType === 'shift') {
      const time = getShiftTime(item);
      if (!time) return;
      startMins = timeToMinutes(time.start);
      endMins = timeToMinutes(time.end);
    } else {
      startMins = item.start_time ? timeToMinutes(item.start_time) : 0;
      endMins = item.end_time ? timeToMinutes(item.end_time) : startMins + 60;
    }

    dragRef.current = { item, itemType, action, startY: e.clientY, startX: e.clientX, origStart: startMins, origEnd: endMins, originalDate };
    setDragState({ itemId: item.id, itemType, action, previewStart: startMins, previewEnd: endMins, targetDate: originalDate });
  }, [shiftTypes]);

  useEffect(() => {
    if (!dragState) return;

    const onMove = (e) => {
      if (!dragRef.current) return;
      const { action, startY, startX, origStart, origEnd } = dragRef.current;
      const deltaY = e.clientY - startY;
      const deltaX = e.clientX - startX;
      if (Math.abs(deltaY) > 4 || Math.abs(deltaX) > 4) didDragRef.current = true;
      const deltaMins = Math.round(deltaY / ROW_HEIGHT) * 30;

      if (action === 'move') {
        const dur = origEnd - origStart;
        let ns = Math.round((origStart + deltaMins) / 30) * 30;
        ns = Math.max(0, Math.min(ns, 1440 - dur));

        // Detect which day column the mouse is over
        const colEls = scrollRef.current?.querySelectorAll('[data-day-col]');
        let targetDate = dragState.targetDate;
        if (colEls) {
          for (const col of colEls) {
            const rect = col.getBoundingClientRect();
            if (e.clientX >= rect.left && e.clientX <= rect.right) {
              targetDate = col.getAttribute('data-day-col');
              break;
            }
          }
        }

        setDragState(prev => ({ ...prev, previewStart: ns, previewEnd: ns + dur, targetDate }));
      } else {
        let ne = Math.round((origEnd + deltaMins) / 30) * 30;
        ne = Math.max(origStart + 30, Math.min(ne, 1440));
        setDragState(prev => ({ ...prev, previewEnd: ne }));
      }
    };

    const onUp = () => {
      if (dragRef.current && dragState && didDragRef.current && onUpdateItemTime) {
        const ns = minutesToTime(dragState.previewStart);
        const ne = minutesToTime(dragState.previewEnd);
        const { item, itemType, originalDate } = dragRef.current;
        const newDate = dragState.targetDate || originalDate;
        onUpdateItemTime(itemType, item.id, ns, ne, newDate !== originalDate ? newDate : null);
      }
      dragRef.current = null;
      setDragState(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragState, onUpdateItemTime]);

  const getPreview = (id, type) => {
    if (!dragState || dragState.itemId !== id || dragState.itemType !== type) return null;
    return { start: dragState.previewStart, end: dragState.previewEnd };
  };

  // ========== RENDER HELPERS ==========
  const renderBlock = (item, itemType, startMins, endMins, colorCls, label, subLabel, onClick, dateStr) => {
    const preview = getPreview(item.id, itemType);
    const isTarget = dragState?.targetDate === dateStr && dragState?.itemId === item.id;
    const isGhost = dragState?.itemId === item.id && dragState?.itemType === itemType && dragState?.targetDate && dragState.targetDate !== dateStr;
    const sM = preview && !isGhost ? preview.start : startMins;
    const eM = preview && !isGhost ? preview.end : endMins;
    const top = (sM / 30) * ROW_HEIGHT;
    const height = Math.max(((eM - sM) / 30) * ROW_HEIGHT, ROW_HEIGHT);
    const isDragging = dragState?.itemId === item.id && dragState?.itemType === itemType;
    const dispStart = preview && !isGhost ? minutesToTime(preview.start) : minutesToTime(startMins);
    const dispEnd = preview && !isGhost ? minutesToTime(preview.end) : minutesToTime(endMins);

    return (
      <div
        key={`${itemType}-${item.id}`}
        className={`absolute left-0.5 right-0.5 rounded border-l-[3px] px-1 py-0.5 overflow-hidden shadow-sm transition-colors ${colorCls} ${isGhost ? 'opacity-25' : isDragging ? 'opacity-80 ring-2 ring-slate-400 z-20' : 'cursor-grab hover:brightness-90'}`}
        style={{ top, height: height - 2 }}
        onMouseDown={(e) => startDrag(e, item, itemType, 'move', dateStr)}
        onClick={(e) => { e.stopPropagation(); if (!didDragRef.current) onClick(); }}
        title={`${label}: ${dispStart}-${dispEnd}`}
        data-testid={`timeline-${itemType}-${item.id}`}
      >
        <p className="text-[9px] font-bold truncate leading-tight">{label}</p>
        {height > ROW_HEIGHT && <p className="text-[8px] opacity-80 font-mono leading-tight">{subLabel || `${dispStart}-${dispEnd}`}</p>}
        <div
          className="absolute bottom-0 left-0 right-0 h-2 cursor-s-resize hover:bg-black/10 rounded-b"
          onMouseDown={(e) => { e.stopPropagation(); startDrag(e, item, itemType, 'resize', dateStr); }}
          data-testid={`resize-${itemType}-${item.id}`}
        />
      </div>
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm flex-1 flex flex-col overflow-hidden" data-testid="week-timeline">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-200 flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={onPrevWeek} data-testid="prev-week-btn"><ChevronLeft className="w-4 h-4" /></Button>
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {format(weekStart, 'dd MMM', { locale: dateLocale })} - {format(weekEnd, 'dd MMM yyyy', { locale: dateLocale })}
        </h2>
        <Button variant="ghost" size="sm" onClick={onNextWeek} data-testid="next-week-btn"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Day headers */}
      <div className="flex border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div className="w-14 flex-shrink-0 border-r border-slate-200" />
        {days.map(day => (
          <div key={day.toString()} className={`flex-1 text-center py-2 border-r border-slate-100 last:border-r-0 min-w-0 ${isToday(day) ? 'bg-red-50' : ''}`}>
            <div className="text-[10px] font-medium text-slate-500 uppercase">{format(day, 'EEE', { locale: dateLocale })}</div>
            <div className={`text-sm font-bold ${isToday(day) ? 'bg-red-400 text-white w-7 h-7 rounded-full flex items-center justify-center mx-auto' : 'text-slate-800'}`}>
              {format(day, 'd')}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="mt-0.5 w-5 h-5 mx-auto rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors" data-testid={`week-add-${format(day, 'yyyy-MM-dd')}`}>
                  <Plus className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>{t("shift_planning")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onAddShift(day)}>{t("add_shift")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAddAbsence(day)}>{t("add_absence")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t("manager")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onAddTask(day)}><ClipboardList className="w-3.5 h-3.5 mr-2 text-amber-500" />{t("add_task")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAddNote(day)}><StickyNote className="w-3.5 h-3.5 mr-2 text-blue-500" />{t("add_note")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAddReminder(day)}><Bell className="w-3.5 h-3.5 mr-2 text-cyan-500" />{t("add_reminder")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {/* Time grid */}
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <div className={`flex relative ${dragState ? 'select-none' : ''}`} style={{ minHeight: TIME_SLOTS.length * ROW_HEIGHT }}>
          {/* Time labels */}
          <div className="w-14 flex-shrink-0 border-r border-slate-200 relative">
            {TIME_SLOTS.map((slot, i) => (
              <div key={slot} className="absolute right-1.5 text-[9px] text-slate-400 font-mono leading-none" style={{ top: i * ROW_HEIGHT - 5 }}>
                {slot.endsWith(':00') ? slot : ''}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayShifts = shifts.filter(s => s.date === dateStr);
            const dayAbsences = absences.filter(a => a.date === dateStr);
            const dayTasks = tasks.filter(tk => tk.due_date === dateStr && !tk.is_completed);
            const dayReminders = reminders.filter(r => r.due_date === dateStr && !r.is_completed);

            return (
              <div key={day.toString()} className={`flex-1 border-r border-slate-100 last:border-r-0 relative min-w-0 ${isToday(day) ? 'bg-red-50/20' : ''} ${dragState?.targetDate === dateStr && dragState?.itemId && !(dayShifts.concat(dayTasks).concat(dayReminders)).find(x=>x.id===dragState.itemId) ? 'bg-blue-50/30' : ''}`}
                data-day-col={dateStr}>
                {/* Grid lines */}
                {TIME_SLOTS.map((slot, i) => (
                  <div key={slot} className={`absolute w-full border-t ${slot.endsWith(':00') ? 'border-slate-200' : 'border-slate-50'}`}
                    style={{ top: i * ROW_HEIGHT }} />
                ))}

                {/* Shifts */}
                {dayShifts.map(shift => {
                  const time = getShiftTime(shift);
                  if (!time) return null;
                  const sm = timeToMinutes(time.start);
                  const em = timeToMinutes(time.end);
                  const worker = getWorkerName(shift.worker_id);
                  return renderBlock(shift, 'shift', sm, em, getShiftColor(shift), worker, `${time.start}-${time.end}`, () => onShiftClick(shift), dateStr);
                })}

                {/* Absences at 08:00 */}
                {dayAbsences.map((abs, idx) => (
                  <div key={abs.id}
                    className="absolute left-0.5 right-0.5 rounded bg-red-100 border border-dashed border-red-300 px-1 text-[8px] text-red-700 truncate shadow-sm"
                    style={{ top: (8 * 2 + idx) * ROW_HEIGHT, height: ROW_HEIGHT - 2 }}
                    title={`${getWorkerName(abs.worker_id)} - ${abs.type}`}
                  >
                    <XCircle className="w-2.5 h-2.5 inline mr-0.5" />{getWorkerName(abs.worker_id)}
                  </div>
                ))}

                {/* Tasks */}
                {dayTasks.map((task, idx) => {
                  if (task.start_time && task.end_time) {
                    const sm = timeToMinutes(task.start_time);
                    const em = timeToMinutes(task.end_time);
                    return renderBlock(task, 'task', sm, em, 'border-amber-400 bg-amber-100 text-amber-800', task.title, null, () => onEditTask(task), dateStr);
                  }
                  const fallbackStart = 18 * 60;
                  const fallbackEnd = fallbackStart + 60;
                  return renderBlock(
                    { ...task, start_time: minutesToTime(fallbackStart), end_time: minutesToTime(fallbackEnd) },
                    'task', fallbackStart, fallbackEnd,
                    'border-amber-300 bg-amber-50 text-amber-700',
                    task.title, null, () => onEditTask(task), dateStr
                  );
                })}

                {/* Reminders */}
                {dayReminders.map((rem, idx) => {
                  if (rem.start_time && rem.end_time) {
                    const sm = timeToMinutes(rem.start_time);
                    const em = timeToMinutes(rem.end_time);
                    return renderBlock(rem, 'reminder', sm, em, 'border-cyan-400 bg-cyan-100 text-cyan-800', rem.client_name, null, () => onEditReminder(rem), dateStr);
                  }
                  const fallbackStart = 19 * 60;
                  const fallbackEnd = fallbackStart + 60;
                  return renderBlock(
                    { ...rem, start_time: minutesToTime(fallbackStart), end_time: minutesToTime(fallbackEnd) },
                    'reminder', fallbackStart, fallbackEnd,
                    'border-cyan-300 bg-cyan-50 text-cyan-700',
                    rem.client_name, null, () => onEditReminder(rem), dateStr
                  );
                })}

                {/* Ghost preview when dragging item TO this day */}
                {dragState && dragState.targetDate === dateStr && dragState.action === 'move' && (() => {
                  const originItems = [...shifts.filter(s => s.date === dragState.targetDate), ...tasks.filter(t => t.due_date === dragState.targetDate), ...reminders.filter(r => r.due_date === dragState.targetDate)];
                  const isNative = originItems.some(it => it.id === dragState.itemId);
                  if (isNative) return null;
                  const top = (dragState.previewStart / 30) * ROW_HEIGHT;
                  const height = Math.max(((dragState.previewEnd - dragState.previewStart) / 30) * ROW_HEIGHT, ROW_HEIGHT);
                  return (
                    <div className="absolute left-0.5 right-0.5 rounded border-2 border-dashed border-blue-400 bg-blue-100/50 z-10 pointer-events-none"
                      style={{ top, height: height - 2 }} />
                  );
                })()}

                {/* Now indicator */}
                {isToday(day) && (
                  <div className="absolute left-0 right-0 z-10 flex items-center pointer-events-none" style={{ top: nowTop }}>
                    <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                    <div className="flex-1 h-[2px] bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
